/**
 * Worklet source-string exports.
 *
 * Built-in modules that rely on AudioWorkletProcessor (currently just
 * BitcrusherModule) need their processor source registered on the
 * AudioContext via `audioWorklet.addModule(url)` BEFORE the module's
 * `init()` is called. Browsers require a real URL; this package ships
 * the source as a string so consumers can build that URL however
 * suits their stack:
 *
 * ```ts
 * import { BITCRUSHER_WORKLET_SOURCE, blobUrlFor } from '@ac-slayer/synth-core/worklets';
 *
 * const engine = new AudioEngine();
 * await engine.unlock();
 * await engine.loadWorklet(blobUrlFor(BITCRUSHER_WORKLET_SOURCE));
 * // …now safe to add a BitcrusherModule to your effects chain.
 * ```
 *
 * The Blob URL approach has zero bundler dependencies — works in Vite,
 * webpack, esbuild, plain `<script type="module">`, anywhere a string
 * import + Blob/URL exist.
 */
export { BITCRUSHER_WORKLET_SOURCE } from './bitcrusher.generated';
export { GRANULAR_WORKLET_SOURCE } from './granular.generated';

/**
 * Convenience helper — turns a worklet source string into a Blob URL
 * suitable for `engine.loadWorklet()`. Returned URLs persist for the
 * lifetime of the page; if you mount and unmount many engines you can
 * call `URL.revokeObjectURL(url)` to free them.
 */
export function blobUrlFor(source: string): string {
  const blob = new Blob([source], { type: 'text/javascript' });
  return URL.createObjectURL(blob);
}
